import { Inviter, Invitation, SessionState, UserAgent, Session } from 'sip.js';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import { LogModule, LogService } from '../services/log.service';

export enum CallDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
}

// Accepts a *single* unfolded header line, e.g.
// P-Asserted-Identity: "Chris" <tel:07748153275;phone-context=chrisdev.com>
// P-Asserted-Identity: <sips:alice@example.com>
// P-Asserted-Identity: <sip:bob@corp.local;user=phone>
export interface ParsedPAI {
  scheme: 'sip' | 'sips' | 'tel';
  uri: string; // Full URI part inside <...> without angle brackets
  displayName?: string; // If present in "..."
  userPart?: string; // before '@' for sip/sips, or the tel number for tel:
  hostPart?: string; // after '@' for sip/sips
  phoneContext?: string; // for tel:;phone-context=...
}

const PAI_SIP_REGEX =
  /^P-Asserted-Identity:\s*(?:"([^"]*)"\s*)?<sips?:([^>]+)>/i;
const PAI_TEL_REGEX =
  /^P-Asserted-Identity:\s*(?:"([^"]*)"\s*)?<tel:([^>;]+)(?:;phone-context=([^;>\s]+))?/i;

// Folded (continued) headers join lines starting with SP/HTAB per RFC 5322
function unfoldHeader(raw: string): string {
  return raw.replace(/\r?\n[ \t]+/g, ' ');
}

export function parsePAssertedIdentity(
  rawHeaderLine: string
): ParsedPAI | null {
  const line = unfoldHeader(rawHeaderLine).trim();

  // tel: form
  {
    const m = line.match(PAI_TEL_REGEX);
    if (m) {
      const display = m[1]?.trim() || undefined;
      const number = m[2]?.trim();
      const phoneCtx = m[3]?.trim() || undefined;
      if (!number) return null;
      return {
        scheme: 'tel',
        uri: phoneCtx
          ? `tel:${number};phone-context=${phoneCtx}`
          : `tel:${number}`,
        displayName: display,
        userPart: number,
        phoneContext: phoneCtx,
      };
    }
  }

  // sip/sips form
  {
    const m = line.match(PAI_SIP_REGEX);
    if (m) {
      const display = m[1]?.trim() || undefined;
      const uri = m[2]?.trim(); // everything after sip: or sips: up to '>'
      if (!uri) return null;
      // Try to split user@host for convenience (best-effort)
      const at = uri.indexOf('@');
      const userPart = at > -1 ? uri.slice(0, at) : undefined;
      const hostPart = at > -1 ? uri.slice(at + 1) : undefined;

      const scheme = line.toLowerCase().includes('<sips:') ? 'sips' : 'sip';
      return {
        scheme,
        uri: `${scheme}:${uri}`,
        displayName: display,
        userPart,
        hostPart,
      };
    }
  }

  return null;
}

function getPAssertedIdentityHeader(session: Session): string | undefined {
  // Incoming INVITE -> Invitation
  const inviteReq = (session as Invitation)?.request;
  if (inviteReq && typeof inviteReq.getHeader === 'function') {
    const header = inviteReq.getHeader('P-Asserted-Identity');
    if (header) return `P-Asserted-Identity: ${header}`;
  }

  // Some stacks/cases keep raw headers on message objects; best-effort fallback:
  const anySession: any = session as any;
  const rawFromAltPaths =
    anySession?.incomingInviteRequest?.message?.headers?.[
      'p-asserted-identity'
    ]?.[0]?.raw ??
    anySession?.outgoingInviteRequest?.message?.headers?.[
      'p-asserted-identity'
    ]?.[0]?.raw;

  if (rawFromAltPaths) return rawFromAltPaths as string;

  return undefined;
}

export class EveCall {
  public direction: CallDirection;
  public remoteUser: string;
  public startTime: Date;
  public isOnHold: boolean = false;
  public duration: number = 0; // In seconds

  // New: session state tracking. Initialized with the session's current state.
  public session: Inviter | Invitation;
  private durationInterval: any = null;

  // --- new for DTMF feedback ---
  private audioContext: AudioContext;
  private dtmfFrequencies: Record<string, [number, number]> = {
    '1': [697, 1209],
    '2': [697, 1336],
    '3': [697, 1477],
    A: [697, 1633],
    '4': [770, 1209],
    '5': [770, 1336],
    '6': [770, 1477],
    B: [770, 1633],
    '7': [852, 1209],
    '8': [852, 1336],
    '9': [852, 1477],
    C: [852, 1633],
    '*': [941, 1209],
    '0': [941, 1336],
    '#': [941, 1477],
    D: [941, 1633],
  };

  constructor(session: Inviter | Invitation, private logService: LogService) {
    this.session = session;
    this.direction =
      session instanceof Invitation
        ? CallDirection.Inbound
        : CallDirection.Outbound;
    const paiHeader = getPAssertedIdentityHeader(session);

    if (paiHeader) {
      const parsed = parsePAssertedIdentity(paiHeader);
      if (parsed) {
        // Choose what you want to display/store.
        // Common choices:
        //  - tel with phone-context -> number@context
        //  - sip/sips -> full URI
        //  - prefer displayName if present, else number/URI
        if (parsed.scheme === 'tel') {
          const pretty = parsed.phoneContext
            ? `${parsed.userPart}@${parsed.phoneContext}`
            : parsed.userPart ?? parsed.uri;
          this.remoteUser = parsed.displayName || pretty || "";
        } else {
          this.remoteUser = parsed.displayName || parsed.uri || "";
        }
      } else {
        // PAI present but couldn't parse; fall back
        this.remoteUser = session.remoteIdentity.uri.toString();
      }
    } else {
      // No PAI; fall back
      this.remoteUser = session.remoteIdentity.uri.toString();
    }

    // this.remoteUser = session.remoteIdentity.uri.toString();
    this.startTime = new Date();

    // Start tracking call duration.
    this.durationInterval = setInterval(() => {
      this.duration = Math.floor(
        (new Date().getTime() - this.startTime.getTime()) / 1000
      );
    }, 1000);

    // init WebAudio
    this.audioContext = new AudioContext();
  }

  async acceptCall(): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.accept();
      console.log(`📞 Call with ${this.remoteUser} accepted.`);
      // Optionally, update the session state manually if needed.
      // This may already be updated by SIP.js.
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to accept call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async rejectCall(): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.reject();
      console.log(`❌ Call with ${this.remoteUser} rejected.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to reject call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async holdCall(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to hold.');
      return;
    }
    try {
      const options = {
        sessionDescriptionHandlerModifiers: [this.holdModifier.bind(this)],
        requestDelegate: {
          onAccept: () => {
            this.isOnHold = true;
            console.log(`✅ Call with ${this.remoteUser} held.`);
          },
          onReject: () => {
            console.warn(`❌ Call hold was rejected by ${this.remoteUser}.`);
            this.isOnHold = false;
          },
        },
      };
      this.isOnHold = true;
      await this.session.invite(options);
      console.log(`⏸️ Call with ${this.remoteUser} put on hold.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to hold call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async unholdCall(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to resume.');
      return;
    }
    const options = {
      sessionDescriptionHandlerModifiers: [],
      requestDelegate: {
        onAccept: () => {
          this.isOnHold = false;
          console.log(`✅ Call with ${this.remoteUser} resumed.`);
        },
        onReject: () => {
          console.warn(`❌ Call resume was rejected by ${this.remoteUser}.`);
          this.isOnHold = true;
        },
      },
    };
    try {
      await this.session.invite(options);
      this.isOnHold = false;
      console.log(`▶️ Call with ${this.remoteUser} resumed.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to unhold call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  /**
   * Hang up this call.
   * - If we’re still in “Establishing”, send a CANCEL
   * - Otherwise send a BYE
   */
  async endCall(): Promise<void> {
    if (!this.session) return;

    try {
      // Outgoing call not yet answered → cancel
      if (
        this.session instanceof Inviter &&
        this.session.state === SessionState.Establishing
      ) {
        await this.session.cancel();
        console.log(`❌ Outgoing call to ${this.remoteUser} canceled.`);
      }
      // Already answered (or in early media) → normal hangup
      else {
        await this.session.bye();
        console.log(`🔚 Call with ${this.remoteUser} ended.`);
      }

      clearInterval(this.durationInterval);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to end call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async sendDTMF(digit: string): Promise<void> {
    console.log(`🔢 Attempting to send RTP DTMF: '${digit}'...`);
    if (!this.session) {
      console.warn('⚠️ No active call session found. Cannot send DTMF.');
      return;
    }

    // play local tone
    this.playLocalDTMF(digit);

    const sessionDescriptionHandler = this.session
      .sessionDescriptionHandler as SessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.warn('⚠️ No SessionDescriptionHandler available for DTMF.');
      return;
    }
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      console.warn('⚠️ No PeerConnection found.');
      return;
    }
    let dtmfSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'audio')?.dtmf;
    if (!dtmfSender) {
      console.log('❌ No DTMF sender found.');
      return;
    }
    try {
      dtmfSender.insertDTMF(digit, 800, 400);
      console.log(`✅ RTP DTMF '${digit}' sent successfully.`);
    } catch (err: any) {
      this.logService.logError(
        `❌ Failed to send DTMF '${digit}': ${err.message}`,
        LogModule.SIP
      );
      throw err;
    }
  }

  async transferTo(target: string, realm: string): Promise<void> {
    if (!this.session) {
      return Promise.reject(new Error('No session to transfer'));
    }

    const sipTarget = target.includes('@')
      ? `sip:${target}`
      : `sip:${target}@${realm}`;

    const targetURI = UserAgent.makeURI(sipTarget);
    if (!targetURI) {
      return Promise.reject(new Error('Invalid target URI'));
    }

    try {
      await this.session.refer(targetURI);
      console.log(
        `🔁 Call with ${this.remoteUser} transferred to ${sipTarget}`
      );
    } catch (err: any) {
      this.logService.logError(
        `❌ Transfer failed for ${this.remoteUser}: ${err.message}`,
        LogModule.SIP
      );
      throw err;
    }
  }

  private async holdModifier(description: any): Promise<any> {
    let sdp = description.sdp;
    sdp = sdp.replace(/a=sendrecv\r\n/g, 'a=sendonly\r\n');
    description.sdp = sdp;
    return description;
  }

  private playLocalDTMF(digit: string, duration = 200) {
    const freqs = this.dtmfFrequencies[digit];
    if (!freqs) return;
    const now = this.audioContext.currentTime;
    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.connect(this.audioContext.destination);

    const [f1, f2] = freqs;
    const o1 = this.audioContext.createOscillator();
    const o2 = this.audioContext.createOscillator();
    o1.frequency.value = f1;
    o2.frequency.value = f2;
    o1.connect(gain);
    o2.connect(gain);
    o1.start(now);
    o2.start(now);
    o1.stop(now + duration / 1000);
    o2.stop(now + duration / 1000);
  }
}
